import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Calendar, Users, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NomadSpotCaseStudy: React.FC = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={goBack}
              className="flex items-center text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Portfolio
            </button>
            <button
              onClick={scrollToTop}
              className="text-primary-color hover:text-secondary-color transition-colors duration-200"
            >
              Scroll to Top
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-background-primary to-background-secondary py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block text-primary-color bg-primary-color/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Map-Based Mobile App
            </span>
            
            <h1 className="text-4xl md:text-6xl font-bold text-text-primary mb-6">
              Nomad Spot
            </h1>
            
            <p className="text-xl text-text-secondary mb-8 leading-relaxed">
              Designed a mobile app concept for digital nomads to discover and rate work-friendly spots, 
              balancing user needs with potential business opportunities.
            </p>

                        {/* Project Stats */}
                                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">📅</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Timeline</h3>
                  <p className="text-text-secondary">2 weeks</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">👥</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Team Size</h3>
                  <p className="text-text-secondary">Solo project</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">👨‍💻</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Role</h3>
                  <p className="text-text-secondary">Product Designer</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">🎨</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Design Tools</h3>
                  <p className="text-text-secondary">Figma, Prototyping, User Flows, User Research, Market Research</p>
                </div>
              </div>

            {/* Hero Image */}
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/projects/covers/cover-nomad-spot.png"
                alt="Nomad Spot App"
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-text-primary mb-8">Project Overview</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-text-primary mb-4">The Challenge</h3>
                  <p className="text-text-secondary leading-relaxed mb-6">
                    As a digital nomad, I often struggle to find good places to work. Existing tools (like Google Maps or generic café apps) provide locations, but fail to answer the questions that really matter to remote workers:
                  </p>
                  <ul className="text-text-secondary leading-relaxed mb-6 space-y-2 list-disc list-inside">
                    <li>Does this café have reliable WiFi and sockets?</li>
                    <li>How noisy is it?</li>
                    <li>Will they ask me to leave if I open a laptop?</li>
                    <li>Is smoking or drinking allowed?</li>
                  </ul>
                  <p className="text-text-secondary leading-relaxed mb-6">
                    Nomad Spot was born as a design exercise to explore how a mobile app could solve these pain points for digital nomads and remote workers — think Google Maps, but built for work.
                  </p>
                  
                  <h4 className="text-lg font-semibold text-text-primary mb-4">Market Research</h4>
                  <p className="text-text-secondary leading-relaxed mb-6">
                    Before starting design, I ran independent market research to understand why existing solutions (like Workfrom, Croissant, or even Google Maps categories) haven't gained mainstream traction. My plan followed five steps:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 bg-white rounded-xl shadow-sm overflow-hidden">
                      <thead>
                        <tr className="bg-primary-color/10">
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-text-primary">Step</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-text-primary">Action</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-text-primary">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 text-text-primary font-medium">1</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Competitive Analysis</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Identify existing apps/services for remote workers & catalog their features (filters, reviews, monetization)</td>
                        </tr>
                        <tr className="bg-background-secondary">
                          <td className="border border-gray-300 px-4 py-3 text-text-primary font-medium">2</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Gap Identification</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Pinpoint what's missing (e.g. social validation, review quality, real work-specific metrics)</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 text-text-primary font-medium">3</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">User Interviews</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Asked fellow digital nomads and remote workers about pain points & extracted unique needs like "Will they kick me out?" or smoking rules</td>
                        </tr>
                        <tr className="bg-background-secondary">
                          <td className="border border-gray-300 px-4 py-3 text-text-primary font-medium">4</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Persona Development</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Created personas around two core motivations: convenience (quick nearest spots) and exploration (discovering new cafés)</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 text-text-primary font-medium">5</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Value Proposition Testing</td>
                          <td className="border border-gray-300 px-4 py-3 text-text-secondary">Validated whether unique features (gamified reviews, sponsored spots, WiFi speed tests) would solve unmet needs and create differentiation</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-text-primary mb-4">The Solution</h3>
                  <p className="text-text-secondary leading-relaxed mb-6">
                    I designed and delivered a mobile app concept tailored for digital nomads. The app combines 
                    real-time location awareness, curated recommendations, and gamified reviews — all while 
                    balancing business potential through sponsored spots.
                  </p>
                  <div className="text-text-secondary leading-relaxed">
                    <p className="font-semibold text-text-primary mb-3">Key contributions:</p>
                    <ul className="space-y-2 list-disc list-inside">
                      <li>Designed high-fidelity Figma mockups in both Light and Dark mode.</li>
                      <li>Iterated on review flows (from long form → gamified cards).</li>
                      <li>Added WiFi speed test and contextual filters (noise, sockets, smoking rules).</li>
                      <li>Conducted informal usability testing with digital nomads, leading to changes in layout, scoring flow, and prioritization.</li>
                      <li>Explored monetization via Sponsored Work Spots in the "Recommended" feed.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-background-secondary">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-text-primary mb-12 text-center">Design Process</h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md text-center">
                  <div className="w-12 h-12 bg-primary-color/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary-color font-bold text-lg">1</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Discovery</h3>
                  <p className="text-text-secondary text-sm">
                    Identified needs through personal experience and user interviews.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md text-center">
                  <div className="w-12 h-12 bg-primary-color/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary-color font-bold text-lg">2</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Definition</h3>
                  <p className="text-text-secondary text-sm">
                    Defined personas: "Quick Fix Nomad" (urgency-driven) and "Explorer Nomad" (curiosity-driven).
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md text-center">
                  <div className="w-12 h-12 bg-primary-color/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary-color font-bold text-lg">3</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Design & Validation</h3>
                  <p className="text-text-secondary text-sm">
                    Designed wireframes → mockups, iterated review flow, added gamification and internet speed test, built Dark Mode.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md text-center">
                  <div className="w-12 h-12 bg-primary-color/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary-color font-bold text-lg">4</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Iteration & Refinement</h3>
                  <p className="text-text-secondary text-sm">
                    Balanced business needs (Sponsored spots) with user trust (clear labels). Refined navigation hierarchy to surface In Your Area first for urgent needs.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-text-primary mb-16 text-center">Key Features</h2>
              
              {/* Landing Page */}
              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">Landing Page</h3>
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div className="space-y-4">
                    <p className="text-text-secondary leading-relaxed">
                      1. <strong>In Your Area:</strong> Immediate local spots (solves urgent "need to find a spot now" scenario).
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      2. <strong>Recommended:</strong> Curated and Sponsored spots for exploration.
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      3. <strong>Search Bar:</strong> Search by name, area, or filter by features (WiFi, sockets, noise).
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/projects/detail/nomad-spot-mobile/landing-page.png" 
                      alt="Nomad Spot Landing Page" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              {/* Category Page */}
              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">Category Page</h3>
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div>
                    <p className="text-text-secondary leading-relaxed">
                      Browse expanded lists within each category (e.g. In your area, Recommended).
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/projects/detail/nomad-spot-mobile/category-page.png" 
                      alt="Nomad Spot Category Page" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              {/* Work Spot Page */}
              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">Work Spot Page</h3>
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div className="space-y-4">
                    <p className="text-text-secondary leading-relaxed">
                      1. Spot details with reviews, gallery, and filters.
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      2. <strong>"Review!" button placed at thumb level for maximum accessibility.</strong>
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      3. Add to Favorites with a heart icon.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/projects/detail/nomad-spot-mobile/work-spot-page.png" 
                      alt="Nomad Spot Work Spot Page" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              {/* Add A Spot */}
              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">Add A Spot</h3>
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div className="space-y-4">
                    <p className="text-text-secondary leading-relaxed">
                      1. Search and suggest spots; fallback to creating new ones.
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      2. Duplicates prevented by checking suggested places under the map.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/projects/detail/nomad-spot-mobile/add-a-spot-page.png" 
                      alt="Nomad Spot Add A Spot Page" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              {/* Review Flow */}
              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">Review Flow</h3>
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div className="space-y-4">
                    <p className="text-text-secondary leading-relaxed">
                      1. Lightweight, gamified scoring (cards per question instead of long forms).
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      2. Questions cover real nomad needs: WiFi, sockets, noise, "Will they kick you out?", smoking/drinking.
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      3. WiFi speed test integrated.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/projects/detail/nomad-spot-mobile/review-flow.png" 
                      alt="Nomad Spot Review Flow" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              {/* Dark Mode */}
              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">Dark Mode</h3>
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div>
                    <p className="text-text-secondary leading-relaxed">
                      Alternate theme for night owls and dim cafés.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/projects/detail/nomad-spot-mobile/dark-mode.png" 
                      alt="Nomad Spot Dark Mode" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>



      {/* Technologies Used */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-text-primary mb-8 text-center">Reflection & Improvements</h2>
              
              <div className="max-w-3xl mx-auto">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-4">This design exercise taught me how to:</h3>
                    <ul className="text-text-secondary leading-relaxed space-y-2 list-disc list-inside">
                      <li>Balance convenience vs. exploration in a single app.</li>
                      <li>Prioritize information hierarchy (urgent needs surfaced first).</li>
                      <li>Integrate business viability without compromising user trust (sponsored spots).</li>
                      <li>Gamify reviews to reduce friction and increase participation.</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-4">If I had more time, I would:</h3>
                    <ul className="text-text-secondary leading-relaxed space-y-2 list-disc list-inside">
                      <li>Build a sponsor-side flow for cafés to self-onboard as Sponsored spots.</li>
                      <li>Add community features (events, meetups, gatherings).</li>
                      <li>Integrate co-working spaces and premium filters.</li>
                      <li>Expand gamified review mechanics (badges, leaderboards).</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-text-primary mb-8">Conclusion</h2>
              <div className="max-w-3xl mx-auto">
                <p className="text-text-secondary leading-relaxed text-lg">
                  This project was less about shipping a real product and more about exploring how design can bridge personal pain points, market gaps, and business opportunities in a cohesive, user-centric way.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-text-primary text-white py-12">
        <div className="container">
          <div className="text-center">
            <p className="text-gray-300 mb-4">
              Ready to work together? Let's create something amazing.
            </p>
            <button
              onClick={() => {
                navigate('/');
                // Wait for navigation to complete, then scroll to contact section
                setTimeout(() => {
                  const contactSection = document.querySelector('#contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="bg-primary-color hover:bg-secondary-color text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NomadSpotCaseStudy;
